import { ReactElement } from 'react';
import { getPanelGroupElement } from 'react-resizable-panels';
import { Outlet } from '@tanstack/react-router';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  SIDEBAR_WIDTH_RIGHT,
  SidebarInset,
  SidebarProvider,
  useAiSidebar
} from '@anytag/shared-atoms';
import { Footer } from '@anytag/shared-molecules';
import { MenuTitle } from '@anytag/router';
import { useGlobalLayoutProps } from '@anytag/shared-hooks';
import { cn } from '@anytag/shared-utils';
import { NavMenuSidebar, NavMenuSidebarProps } from './nav-menu-sidebar';
import { AiAssistantSidebar } from './ai-assistant-sidebar';
import { HeaderMenu } from './menu';

export interface LayoutProps {
  title?: MenuTitle;
  className?: string;
  element?: ReactElement;
  hasCombinedTerms?: boolean;
  menuType?: NavMenuSidebarProps['type'];
}

const PANEL_GROUP_ID = 'layout-panel-group';

export const Layout = ({ title: menuTitle, element, className, menuType, hasCombinedTerms }: LayoutProps) => {
  const {
    globalLayout: { hasFailedSubscriptionPayment }
  } = useGlobalLayoutProps();
  const { isAiSidebarOpen } = useAiSidebar();

  // Panel group only support percentage size, so we need to calculate the default size based on the width of the panel group
  const groupElement = getPanelGroupElement(PANEL_GROUP_ID);
  const width = groupElement?.getBoundingClientRect().width;
  const defaultAiSidebarSize = Math.round(width ? (parseInt(SIDEBAR_WIDTH_RIGHT) / width) * 100 : 0);

  return (
    <SidebarProvider
      keyboardShortcuts={{ 'nav-menu': 'b', 'ai-assistant': 'i' }}
      className={cn('flex min-h-full', className)}
      style={{ '--sidebar-width-icon': '40px' } as React.CSSProperties}
    >
      <NavMenuSidebar activeTitle={menuTitle} type={menuType} />

      <ResizablePanelGroup className="h-screen" id={PANEL_GROUP_ID} autoSaveId={PANEL_GROUP_ID} direction="horizontal">
        <ResizablePanel className="h-screen overflow-auto!">
          <SidebarInset
            className={cn(
              'layout-wrapper min-h-full w-auto min-w-0 flex-1 scroll-smooth bg-light-gray px-4 pb-4',
              hasFailedSubscriptionPayment ? 'bg-black-70' : 'bg-light-gray'
            )}
          >
            {/*{!hasCustomHeader && !hasFailedSubscriptionPayment ? <Header /> : null}*/}
            <HeaderMenu />
            <div
              className={cn(
                'flex flex-col gap-4 *:page-card *:rounded-[5px] *:bg-white',
                '*:data-card-connect-bottom:rounded-b-none',
                '*:data-card-connect-top:rounded-t-none',
                '*:data-card-connect-top:-mt-4'
              )}
            >
              <Outlet />
            </div>
            {!hasFailedSubscriptionPayment ? <Footer hasCombinedTerms={hasCombinedTerms} /> : null}
          </SidebarInset>
        </ResizablePanel>

        {isAiSidebarOpen && defaultAiSidebarSize ? (
          <>
            <ResizableHandle className="w-1 bg-transparent" />

            <ResizablePanel
              minSize={0}
              maxSize={80}
              defaultSize={defaultAiSidebarSize}
              className="max-w-[80vw] min-w-[20vw]"
            >
              <AiAssistantSidebar />
            </ResizablePanel>
          </>
        ) : null}
      </ResizablePanelGroup>
    </SidebarProvider>
  );
};
